// @ts-nocheck
// Disposition card — cti-v3-style compact disposition surface that
// sits below the CallScriptCard during (or after) a live call. Wraps
// the existing disposition submit flow that CTIAgentUI already owns.
//
// Data sources — all from the app's existing state, no new endpoint:
//   • customerHistory      — API_CUSTOMER_DISPOSITION_HISTORY entries for
//                            the current caller (already resolved by the
//                            parent via the shared useCustomerHistoryCache
//                            — the SAME cache Dialer.tsx's recent-calls
//                            remark chip reads from), used for the
//                            "Previously saved on this caller" strip.
//                            /cti2-only (gated on `showTabs`, same as the
//                            Live/Pending tabs) — production's IdleScreen.tsx
//                            call site doesn't fetch this, so it keeps
//                            using its own original `callRecords` fallback
//                            below unchanged.
//   • callRecords          — production's IdleScreen.tsx only (agentCallsData.
//                            callRecords — this agent's own call log, which
//                            doesn't carry real disposition remarks, just
//                            whatever ad-hoc note fields a record happens to
//                            have — kept only so that call site's original
//                            behavior doesn't regress; /cti2 ignores it).
//   • remarkStatusList     — the campaign / group's outcome list (or
//                            a sensible fallback matching cti-v3).
//   • dispositionMeta      — the current-call context (call_sid,
//                            callerNumber, etc.) — carried in the
//                            store already, exposed here via props.
//   • onSubmit             — existing submit handler CTIAgentUI wires
//                            to the disposition create / wrap-up
//                            complete flow.
//
// This card is intentionally *slimmer* than DispositionScreen — it
// replaces the KPI-style full disposition view with an inline
// workspace strip. The disposition form's own custom/required fields
// (dispositionMeta.fields[]) render right here too (see the "Details"
// section below, ported from DispositionScreen.tsx's field-type switch) —
// there's no separate full-screen fallback in /cti2, so this is the only
// place an agent can fill them.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { FiEdit3, FiClock, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { format } from 'date-fns';
import { useCallStore } from '@/router/zustand/callStore';
import { formatSeconds, ContactNumberInput, callRowCustomerNumber } from '../ctiHelpers';
import { maskNumber } from '@/utilities/cliDisplay';
import { DatePicker } from '@/components/ui/date-picker';
import { getUser } from '@/utilities/setting';

const FALLBACK_OUTCOMES = [
  'Converted',
  'Interested',
  'Callback',
  'Follow-up',
  'Not Interested',
  'Wrong Number',
  'No Response',
];

const FOLLOW_UP_TIME_SLOTS = (() => {
  const slots = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 5) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const h12 = h % 12 === 0 ? 12 : h % 12;
    slots.push({ value, label: `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}` });
  }
  return slots;
})();

function formatCompactDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date} · ${time}`;
}

function lastTen(n) {
  return String(n || '').replace(/\D/g, '').slice(-10);
}

function formatMmSs(seconds) {
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

// Same required/min/max/type checks useDispositionFlow's validateDisposition
// runs for the live call — duplicated here (rather than imported across the
// cti/cti2 boundary) because a Pending row validates a *different* call's
// field values (item.context.values), not the live dispositionValues the
// hook closes over.
function validateCustomFields(fields, values) {
  const nextErrors = {};
  if (!fields?.length) return nextErrors;
  const getNum = (val) => {
    if (val === null || val === undefined || val === '') return null;
    const n = Number(val);
    return Number.isFinite(n) ? n : null;
  };
  const CONTACT_COLUMN_KEYS = new Set(['firstname', 'primarynumber']);
  const normalizeKey = (name) => String(name || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  fields.forEach((f) => {
    const name = String(f?.field_name || '').trim();
    if (!name) return;
    const value = values?.[name];
    const isEmpty = value === null || value === undefined || String(value).trim() === '';
    if (f?.is_required && isEmpty) { nextErrors[name] = 'Required'; return; }
    if (CONTACT_COLUMN_KEYS.has(normalizeKey(name)) && isEmpty) { nextErrors[name] = 'Required for phonebook'; return; }
    if (String(value || '').trim() === '') return;
    if (f?.field_type === 'Integer' || f?.field_type === 'Float') {
      const n = getNum(value);
      if (n === null) { nextErrors[name] = 'Invalid number'; return; }
      if (f?.field_type === 'Integer' && !Number.isInteger(n)) { nextErrors[name] = 'Must be an integer'; return; }
      const minN = getNum(f?.field_min);
      const maxN = getNum(f?.field_max);
      if (typeof minN === 'number' && n < minN) { nextErrors[name] = `Min ${minN}`; return; }
      if (typeof maxN === 'number' && n > maxN) { nextErrors[name] = `Max ${maxN}`; return; }
    }
    if (f?.field_type === 'Date') {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) { nextErrors[name] = 'Invalid date'; return; }
    }
  });
  return nextErrors;
}

function followUpBlockedFor(values) {
  if (!values?.follow_up_enabled) return false;
  const dateStr = values?.follow_up_date;
  const timeStr = values?.follow_up_time;
  if (!dateStr || !timeStr) return true;
  const normalized = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  const scheduled = new Date(`${dateStr}T${normalized}`);
  return Number.isNaN(scheduled.getTime()) || scheduled.getTime() < Date.now();
}

function FollowUpFields({ values, errors, onValues, onErrors }) {
  const setErrors = (patch) => onErrors?.((prev) => {
    const next = { ...(prev || {}) };
    Object.entries(patch).forEach(([k, v]) => { if (v) next[k] = v; else delete next[k]; });
    return next;
  });
  const checkNotPast = (dateStr, timeStr) => {
    if (!dateStr || !timeStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr) || !/^\d{2}:\d{2}$/.test(timeStr)) return;
    const scheduled = new Date(`${dateStr}T${timeStr}`);
    const past = !Number.isNaN(scheduled.getTime()) && scheduled.getTime() < Date.now();
    setErrors({ follow_up_time: past ? 'Follow-up cannot be in the past' : null });
  };
  const timeSlots = useMemo(() => {
    const dateStr = values?.follow_up_date;
    if (!dateStr || dateStr !== format(new Date(), 'yyyy-MM-dd')) return FOLLOW_UP_TIME_SLOTS;
    const now = new Date();
    const minutesNow = now.getHours() * 60 + now.getMinutes();
    return FOLLOW_UP_TIME_SLOTS.filter((slot) => {
      const [h, m] = slot.value.split(':').map(Number);
      return h * 60 + m > minutesNow;
    });
  }, [values?.follow_up_date]);

  return (
    <div className="dpc-section">
      <label className="dpc-field-check">
        <input
          type="checkbox"
          checked={Boolean(values?.follow_up_enabled)}
          onChange={(e) => {
            const checked = e.target.checked;
            onValues?.({
              follow_up_enabled: checked,
              ...(checked ? {} : { follow_up_date: '', follow_up_time: '' }),
            });
            setErrors({ follow_up_date: null, follow_up_time: null });
          }}
        />
        Set follow-up
      </label>
      {values?.follow_up_enabled ? (
        <div className="dpc-field-grid dpc-field-grid--2col" style={{ marginTop: 8 }}>
          <div className="dpc-field">
            <label className="dpc-field-label">
              Follow-up date<span className="dpc-field-required">*</span>
            </label>
            <DatePicker
              className="dpc-field-input"
              contentClassName="z-[1100]"
              fromDate={new Date()}
              placeholder="Pick a date"
              value={values?.follow_up_date ? new Date(values.follow_up_date) : null}
              onChange={(date) => {
                const nextDate = date ? format(date, 'yyyy-MM-dd') : '';
                onValues?.({ follow_up_date: nextDate });
                setErrors({ follow_up_date: null });
                checkNotPast(nextDate, values?.follow_up_time);
              }}
            />
            {errors?.follow_up_date ? (
              <div className="dpc-field-error">{errors.follow_up_date}</div>
            ) : null}
          </div>
          <div className="dpc-field">
            <label className="dpc-field-label">
              Follow-up time<span className="dpc-field-required">*</span>
            </label>
            <select
              className="dpc-field-input"
              value={values?.follow_up_time || ''}
              onChange={(e) => {
                const nextTime = e.target.value;
                onValues?.({ follow_up_time: nextTime });
                setErrors({ follow_up_time: null });
                checkNotPast(values?.follow_up_date, nextTime);
              }}
            >
              <option value="">Pick a time</option>
              {values?.follow_up_time
                && !FOLLOW_UP_TIME_SLOTS.some((s) => s.value === values.follow_up_time) ? (
                <option value={values.follow_up_time}>
                  {values.follow_up_time}
                </option>
              ) : null}
              {timeSlots.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            {errors?.follow_up_time ? (
              <div className="dpc-field-error">{errors.follow_up_time}</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// One disposition form field, rendered the same way for the live card and
// a reopened Pending row — only the value/onChange/error plumbing differs.
function DispositionFieldInput({ field, value, originalValue, error, maskNumbers, onChange }) {
  const name = String(field?.field_name || '').trim();
  if (!name) return null;
  const type = field?.field_type || 'String';
  const required = Boolean(field?.is_required);
  const isContactNumberField = name.toLowerCase().replace(/[\s_-]+/g, '') === 'contactnumber';
  const options = Array.isArray(field?.options) ? field.options : [];
  const inputType = type === 'Integer' || type === 'Float' ? 'number' : type === 'Date' ? 'date' : 'text';
  const step = type === 'Integer' ? 1 : type === 'Float' ? 'any' : undefined;
  const min = typeof field?.field_min === 'number' ? field.field_min : undefined;
  const max = typeof field?.field_max === 'number' ? field.field_max : undefined;

  return (
    <div className={`dpc-field${error ? ' dpc-field--error' : ''}`}>
      <label className="dpc-field-label">
        {name}
        {required ? <span className="dpc-field-required">*</span> : null}
      </label>
      {type === 'Boolean' ? (
        <label className="dpc-field-check">
          <input
            type="checkbox"
            checked={Boolean(value)}
            disabled={isContactNumberField}
            onChange={(e) => !isContactNumberField && onChange(e.target.checked)}
          />
          Yes
        </label>
      ) : type === 'Date' ? (
        // Calendar-only — no typing a date in manually (agents were
        // entering malformed/inconsistent values via the native text
        // caret), so this is a read-only trigger that opens the same
        // calendar popover the date-range filters use.
        <DatePicker
          className="dpc-field-input"
          contentClassName="z-[1100]"
          value={value ? new Date(value) : null}
          onChange={(date) => onChange(date ? format(date, 'yyyy-MM-dd') : '')}
          placeholder="Pick a date"
        />
      ) : type === 'Textarea' ? (
        <textarea
          className="dpc-field-input dpc-field-textarea"
          rows={3}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : type === 'Multi Select' ? (
        <select
          className="dpc-field-input"
          multiple
          disabled={isContactNumberField}
          value={
            Array.isArray(value)
              ? value
              : typeof value === 'string'
                ? value.split(',').map((v) => v.trim())
                : []
          }
          onChange={(e) => {
            if (isContactNumberField) return;
            onChange(Array.from(e.target.selectedOptions, (o) => o.value.trim()));
          }}
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : isContactNumberField ? (
        <ContactNumberInput
          className="dpc-field-input"
          value={value}
          originalValue={originalValue}
          maskNumbers={maskNumbers}
          onChange={onChange}
        />
      ) : (
        <input
          className="dpc-field-input"
          type={inputType}
          step={step}
          min={min}
          max={max}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {error ? <div className="dpc-field-error">{error}</div> : null}
    </div>
  );
}

export default function DispositionCard({
  dispositionMeta,
  dispositionValues,
  setDispositionValues,
  dispositionErrors = {},
  setDispositionErrors,
  remarkStatusList = [],
  dispositionSubmitting = false,
  onSubmit,
  onSubmitAndNext,
  onSubmitAndExit,
  editingSubmitted = false,
  onEditSubmitted,
  onCancelEditSubmitted,
  customerHistory = [],
  callRecords = [],
  userData,
  dispositionSecondsLeft = null,
  dispositionTotalSeconds = null,
  lastSubmittedDisposition = null,
  pendingDispositions = [],
  onSubmitPending,
  onSubmitPendingAndNext,
  maskNumbers = false,
}) {
  const callerContact  = useCallStore((s) => s.callerContact);
  const activeCallMeta = useCallStore((s) => s.activeCallMeta);
  const callState      = useCallStore((s) => s.callState);
  const isActive       = callState && callState !== 'idle';
  const showTabs        = typeof onSubmitPending === 'function';

  // Card visibility — production's IdleScreen.tsx call site (showTabs
  // false) keeps its exact original behavior: hidden unless a disposition
  // context exists or the agent is mid-call. /cti2 (showTabs true) never
  // hides the section at all — an empty Live tab shows a "No disposition"
  // placeholder instead (see the render below), so the agent always knows
  // where to look. Checked AFTER every hook below is declared (not here) —
  // this used to bail out before the useMemo/useCallback/useState calls
  // that follow, so a transition from "genuinely idle" (early return, ~4
  // hooks) to "active" (full render, ~9 hooks) on the SAME mounted
  // instance violated the Rules of Hooks ("Rendered more hooks than during
  // the previous render"), surfacing the moment a call actually started
  // ringing while this card was already mounted idle.
  const hasDispContext = Boolean(dispositionMeta);

  const currentNumber = useMemo(() => {
    return activeCallMeta?.customer_number
      || activeCallMeta?.callee_number
      || activeCallMeta?.caller_number
      || dispositionMeta?.callerNumber
      || callerContact?.primary_number
      || '';
  }, [activeCallMeta, dispositionMeta, callerContact]);

  // Previous notes for this caller. /cti2 (showTabs true): real disposition
  // history from API_CUSTOMER_DISPOSITION_HISTORY, resolved by the parent
  // for the current caller number — up to the last 10, each entry's own
  // call direction folded into the same meta line as the date/agent (not a
  // new line) so a row's height never changes with or without it.
  // Production's IdleScreen.tsx (showTabs false): unchanged original
  // behavior — derived client-side from this agent's own call log
  // (agentCallsData.callRecords), which carries no real disposition remark
  // field, just whatever ad-hoc note fields a record happens to have.
  const previousNotes = useMemo(() => {
    if (showTabs) {
      return (customerHistory || [])
        .map((h) => {
          const statuses = Array.isArray(h?.remarks_status) ? h.remarks_status.filter(Boolean) : [];
          const note = statuses.join(', ');
          if (!note) return null;
          return {
            id: h?.id || h?.call_sid || h?.created_at,
            when: formatCompactDateTime(h?.created_at),
            agent: h?.agent_name || h?.agent || userData?.username || '',
            direction: h?.call_direction || h?.direction || '',
            note,
          };
        })
        .filter(Boolean)
        .slice(0, 10);
    }
    const key = lastTen(currentNumber);
    if (!key) return [];
    return (callRecords || [])
      .filter((r) => {
        const num = callRowCustomerNumber(r);
        return lastTen(num) === key;
      })
      .map((r) => ({
        id: r?.id,
        when: formatCompactDateTime(r?.time_of_call || r?.created_at),
        agent: r?.agent_name || r?.agent || userData?.username || '',
        note: r?.disposition_note || r?.note || r?.remark || r?.category || '',
      }))
      .filter((r) => r.note)
      .slice(0, 5);
  }, [showTabs, customerHistory, callRecords, currentNumber, userData]);

  // Outcome options — take remark-status list if the campaign supplies
  // one; otherwise show the cti-v3 fallback set.
  const outcomes = useMemo(() => {
    if (Array.isArray(remarkStatusList) && remarkStatusList.length > 0) {
      return remarkStatusList.map((r) => r?.remark || r?.name || String(r)).filter(Boolean);
    }
    return FALLBACK_OUTCOMES;
  }, [remarkStatusList]);

  // Remarks Status is multi-select (checkboxes) PLUS free-typed "Others"
  // text — ported from DispositionScreen.tsx's production behavior:
  // checked boxes and comma-separated Others entries all merge into one
  // dispositionValues.remarks_status array. The old single-pick chip UI
  // here only ever wrote one entry, silently dropping this.
  const selectedOutcomes = useMemo(() => {
    const list = Array.isArray(dispositionValues?.remarks_status) ? dispositionValues.remarks_status : [];
    return list.filter((v) => outcomes.includes(v));
  }, [dispositionValues?.remarks_status, outcomes]);

  const [othersText, setOthersText] = useState('');
  useEffect(() => {
    const list = Array.isArray(dispositionValues?.remarks_status) ? dispositionValues.remarks_status : [];
    setOthersText(list.filter((v) => !outcomes.includes(v)).join(', '));
    // Reset only when a new call/disposition opens, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispositionMeta?.call_sid, editingSubmitted]);

  const commitOutcomes = useCallback((nextSelected, customText) => {
    const extra = String(customText || '').split(',').map((v) => v.trim()).filter(Boolean);
    const merged = [...new Set([...nextSelected, ...extra])];
    setDispositionValues?.((v) => ({ ...(v || {}), remarks_status: merged }));
  }, [setDispositionValues]);

  const toggleOutcome = useCallback((label) => {
    const picked = selectedOutcomes.includes(label)
      ? selectedOutcomes.filter((v) => v !== label)
      : [...selectedOutcomes, label];
    commitOutcomes(picked, othersText);
  }, [selectedOutcomes, othersText, commitOutcomes]);

  // The disposition form's own required/custom fields (dispositionMeta.
  // fields[], from the admin-configured disposition-forms feature) —
  // ported from DispositionScreen.tsx (production's full-screen version),
  // which is the only place this used to render.
  const fields = dispositionMeta?.fields || [];

  const clearFieldError = useCallback((name) => {
    if (!name || !dispositionErrors?.[name]) return;
    setDispositionErrors?.((prev) => {
      const next = { ...(prev || {}) };
      delete next[name];
      return next;
    });
  }, [dispositionErrors, setDispositionErrors]);

  const [historyOpen, setHistoryOpen] = useState(false);

  const [tab, setTab] = useState(() => ((isActive || hasDispContext) ? 'live' : 'pending'));

  useEffect(() => {
    if (dispositionMeta) setTab('live');
  }, [dispositionMeta]);

  const [expandedPendingId, setExpandedPendingId] = useState(null);
  const [pendingEditValues, setPendingEditValues] = useState<any>({});
  const [pendingOthersText, setPendingOthersText] = useState<any>({});
  const [pendingFieldErrors, setPendingFieldErrors] = useState<any>({});
  const [pendingRowSubmittingId, setPendingRowSubmittingId] = useState(null);

  const togglePendingRow = useCallback((item) => {
    setExpandedPendingId((cur) => (cur === item.id ? null : item.id));
    setPendingEditValues((prev) => {
      if (prev[item.id]) return prev;
      // Seed from everything already saved for this item (custom-field
      // values included, prefilled when the disposition first opened) —
      // not just remarks_status/remarks — so the "Details" section below
      // has something to show and edit, same as the live card.
      const saved = item?.context?.values || {};
      const savedStatus = Array.isArray(saved.remarks_status) ? saved.remarks_status : [];
      setPendingOthersText((prevOthers) => ({
        ...prevOthers,
        [item.id]: savedStatus.filter((v) => !outcomes.includes(v)).join(', '),
      }));
      return {
        ...prev,
        [item.id]: {
          ...saved,
          remarks_status: savedStatus,
          remarks: '',
        },
      };
    });
  }, [outcomes]);

  const setPendingRowValue = useCallback((itemId, patch) => {
    setPendingEditValues((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || { remarks_status: [], remarks: '' }), ...patch } }));
  }, []);

  const commitPendingOutcomes = useCallback((itemId, nextSelected, customText) => {
    const extra = String(customText || '').split(',').map((v) => v.trim()).filter(Boolean);
    const merged = [...new Set([...nextSelected, ...extra])];
    setPendingRowValue(itemId, { remarks_status: merged });
  }, [setPendingRowValue]);

  const clearPendingFieldError = useCallback((itemId, name) => {
    setPendingFieldErrors((prev) => {
      if (!prev[itemId]?.[name]) return prev;
      const nextItem = { ...prev[itemId] };
      delete nextItem[name];
      return { ...prev, [itemId]: nextItem };
    });
  }, []);

  const submitPendingRowWith = useCallback(async (item, submitFn) => {
    const values = pendingEditValues[item.id] || { remarks_status: [], remarks: '' };
    const fieldErrors = validateCustomFields(item?.context?.meta?.fields, values);
    if (item?.context?.meta?.dispositionForm?.follow_ups_enabled && followUpBlockedFor(values)) {
      fieldErrors.follow_up_time = 'Pick a follow-up date and time in the future';
    }
    if (Object.keys(fieldErrors).length > 0) {
      setPendingFieldErrors((prev) => ({ ...prev, [item.id]: fieldErrors }));
      return;
    }
    setPendingRowSubmittingId(item.id);
    let ok = false;
    try { ok = await submitFn?.(item, values); }
    finally { setPendingRowSubmittingId(null); }
    if (ok) {
      setExpandedPendingId(null);
      setPendingEditValues((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
      setPendingFieldErrors((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
    }
  }, [pendingEditValues]);

  const submitPendingRow = useCallback(
    (item) => submitPendingRowWith(item, onSubmitPending),
    [submitPendingRowWith, onSubmitPending],
  );

  const submitPendingRowAndNext = useCallback(
    (item) => submitPendingRowWith(item, onSubmitPendingAndNext || onSubmitPending),
    [submitPendingRowWith, onSubmitPendingAndNext, onSubmitPending],
  );

  const followUpBlocked = followUpBlockedFor(dispositionValues);

  const canSave = Array.isArray(dispositionValues?.remarks_status)
    && dispositionValues.remarks_status.length > 0
    && !followUpBlocked;
  const saveHint = canSave
    ? ''
    : (followUpBlocked
      ? 'Pick a follow-up date and time in the future'
      : 'Pick at least one outcome to enable saving');

  const hasCountdown = typeof dispositionSecondsLeft === 'number' && Number.isFinite(dispositionSecondsLeft);
  const countdownLow = hasCountdown && dispositionSecondsLeft <= 10;

  const submit = useCallback(() => {
    if (!canSave || dispositionSubmitting) return;
    onSubmit?.();
  }, [canSave, dispositionSubmitting, onSubmit]);

  const submitAndNext = useCallback(() => {
    if (!canSave || dispositionSubmitting || isActive) return;
    if (onSubmitAndNext) onSubmitAndNext();
    else onSubmit?.();  // fallback: same as save & close
  }, [canSave, dispositionSubmitting, isActive, onSubmit, onSubmitAndNext]);
  const saveMain = useCallback(() => {
    if (!canSave || dispositionSubmitting) return;
    if (editingSubmitted || isActive || !onSubmitAndNext) onSubmit?.();
    else onSubmitAndNext();
  }, [canSave, dispositionSubmitting, editingSubmitted, isActive, onSubmit, onSubmitAndNext]);

  const canSaveAndExit = !isActive && hasCountdown && dispositionSecondsLeft > 1;
  const saveAndExit = useCallback(() => {
    if (!canSave || dispositionSubmitting) return;
    onSubmitAndExit?.();
  }, [canSave, dispositionSubmitting, onSubmitAndExit]);

  // Now safe — every hook above has already run unconditionally.
  if (getUser()?.agentData?.access === 'team_lead') return null;
  if (!showTabs && !isActive && !hasDispContext) return null;
  // Nothing to show: no disposition for this call (by-context returned no
  // form), nothing just saved on it, and no pending items to work through —
  // hide the whole card instead of an empty "No disposition" shell.
  if (!hasDispContext && !lastSubmittedDisposition && pendingDispositions.length === 0) return null;

  return (
    <div className="dpc-card shrink-0">
      {/* Header */}
      <div className="dpc-head">
        <span className="dpc-mark" aria-hidden><FiEdit3 size={14} /></span>
        <div className="dpc-title-wrap">
          <div className="dpc-title">{dispositionMeta?.dispositionForm?.name || 'Disposition'}</div>
          <div className="dpc-sub">{editingSubmitted ? 'Editing saved disposition' : 'Opens automatically with every call'}</div>
        </div>
        {showTabs && tab === 'live' && hasCountdown && (
          <span
            className={`dpc-countdown${countdownLow ? ' dpc-countdown--low' : ''}`}
            title={
              typeof dispositionTotalSeconds === 'number' && dispositionTotalSeconds > 0
                ? `This form auto-moves to Pending ${formatMmSs(dispositionTotalSeconds)} after opening.`
                : undefined
            }
          >
            {formatMmSs(dispositionSecondsLeft)} left
          </span>
        )}
        {showTabs && (
          <div className="csc-tabs" role="tablist">
            <button
              type="button"
              disabled={!isActive && !hasDispContext}
              title={!isActive && !hasDispContext ? 'Live opens automatically once a call starts or a pending item is opened' : undefined}
              className={`csc-tab${tab === 'live' ? ' csc-tab--active' : ''}`}
              onClick={() => setTab('live')}
            >
              Live
            </button>
            <button
              type="button"
              className={`csc-tab${tab === 'pending' ? ' csc-tab--active' : ''}${pendingDispositions.length > 0 ? ' dpc-blink' : ''}`}
              title={pendingDispositions.length > 0 ? 'You skipped a disposition - it is waiting here' : undefined}
              onClick={() => setTab('pending')}
            >
              Pending{pendingDispositions.length > 0 ? ` (${pendingDispositions.length})` : ''}
            </button>
          </div>
        )}
      </div>

      {tab === 'pending' ? (
        <div className="dpc-pending">
          {pendingDispositions.length === 0 ? (
            <div className="dpc-pending-empty">No pending dispositions - you're all caught up.</div>
          ) : (
            [...pendingDispositions]
              .sort((a, b) => {
                const ts = (x) => { const t = new Date(x?.created_at || x?.expires_at || 0).getTime(); return Number.isNaN(t) ? 0 : t; };
                return ts(b) - ts(a);
              })
              .map((item) => {
                const meta = item?.context?.meta || {};
                const formName = meta?.dispositionForm?.name || meta?.campaign_name || 'Disposition';
                const rawNumber = meta?.callerNumber || item?.call_sid || 'Unknown number';
                const number = maskNumbers && meta?.callerNumber ? maskNumber(rawNumber) : rawNumber;
                const when = formatCompactDateTime(item?.created_at || item?.expires_at);
                const expanded = expandedPendingId === item.id;
                const rowValues = pendingEditValues[item.id] || { remarks_status: [], remarks: '' };
                const rowSelectedOutcomes = Array.isArray(rowValues.remarks_status)
                  ? rowValues.remarks_status.filter((v) => outcomes.includes(v))
                  : [];
                const rowHasOutcome = Array.isArray(rowValues.remarks_status) && rowValues.remarks_status.length > 0;
                const rowOthersText = pendingOthersText[item.id] || '';
                const rowSubmitting = pendingRowSubmittingId === item.id;
                const rowFields = meta?.fields || [];
                const rowErrors = pendingFieldErrors[item.id] || {};
                const rowFollowUps = Boolean(meta?.dispositionForm?.follow_ups_enabled);
                const rowFollowUpBlocked = rowFollowUps && followUpBlockedFor(rowValues);
                return (
                  <div className="dpc-pending-item" key={item.id}>
                    <button
                      type="button"
                      className={`dpc-pending-row${expanded ? '' : ' dpc-pending-row--alert'}`}
                      onClick={() => togglePendingRow(item)}
                      title="Open to fill and save this disposition"
                      aria-expanded={expanded}
                    >
                      <span className="dpc-pending-row__main">
                        <span className="dpc-pending-row__title">{formName}</span>
                        <span className="dpc-pending-row__num">{number}</span>
                      </span>
                      {when && <span className="dpc-pending-row__when">{when}</span>}
                      <FiChevronRight size={14} className={`dpc-pending-row__chev${expanded ? ' dpc-pending-row__chev--open' : ''}`} />
                    </button>
                    {expanded && (
                      <div className="dpc-pending-form">
                        <div className="dpc-outcomes">
                          {outcomes.map((o) => (
                            <button
                              key={o}
                              type="button"
                              onClick={() => {
                                const picked = rowSelectedOutcomes.includes(o)
                                  ? rowSelectedOutcomes.filter((v) => v !== o)
                                  : [...rowSelectedOutcomes, o];
                                commitPendingOutcomes(item.id, picked, rowOthersText);
                              }}
                              className={`dpc-outcome${rowSelectedOutcomes.includes(o) ? ' dpc-outcome--active' : ''}`}
                            >
                              {o}
                            </button>
                          ))}
                        </div>
                        <input
                          type="text"
                          className="dpc-field-input"
                          style={{ marginTop: 8 }}
                          placeholder="Others (comma separated)"
                          value={rowOthersText}
                          onChange={(e) => {
                            setPendingOthersText((prev) => ({ ...prev, [item.id]: e.target.value }));
                            commitPendingOutcomes(item.id, rowSelectedOutcomes, e.target.value);
                          }}
                        />

                        {rowFields.length > 0 && (
                        <div className="dpc-field-grid" style={{ marginTop: 10 }}>
                          {rowFields.map((field) => {
                              const name = String(field?.field_name || '').trim();
                              if (!name) return null;
                              return (
                                <DispositionFieldInput
                                  key={name}
                                  field={field}
                                  value={rowValues[name]}
                                  originalValue={item?.context?.values?.[name]}
                                  error={rowErrors[name]}
                                  maskNumbers={maskNumbers}
                                  onChange={(next) => {
                                    setPendingRowValue(item.id, { [name]: next });
                                    clearPendingFieldError(item.id, name);
                                  }}
                                />
                              );
                            })}
                        </div>
                        )}

                        {rowFollowUps ? (
                          <FollowUpFields
                            values={rowValues}
                            errors={rowErrors}
                            onValues={(patch) => setPendingRowValue(item.id, patch)}
                            onErrors={(update) => setPendingFieldErrors((prev) => ({ ...prev, [item.id]: update(prev[item.id]) }))}
                          />
                        ) : null}

                        <div className="dpc-pending-form__foot">
                          <button
                            type="button"
                            onClick={() => submitPendingRowAndNext(item)}
                            disabled={!rowHasOutcome || rowFollowUpBlocked || rowSubmitting}
                            className="dpc-save dpc-save--primary"
                          >
                            {rowSubmitting ? 'Saving…' : 'Save & Next Call'}
                          </button>
                          {/* <button
                            type="button"
                            onClick={() => submitPendingRow(item)}
                            disabled={!rowHasOutcome || rowFollowUpBlocked || rowSubmitting}
                            className="dpc-save dpc-save--primary"
                          >
                            {rowSubmitting ? 'Saving…' : 'Save disposition'}
                          </button>
                          {item?.context?.meta?.is_lead && !item?.context?.meta?.is_lead_recall && (
                            <button
                              type="button"
                              onClick={() => submitPendingRowAndNext(item)}
                              disabled={!rowHasOutcome || rowFollowUpBlocked || rowSubmitting}
                              className="dpc-save"
                            >
                              Save Dispose & Next Lead
                            </button>
                          )} */}
                          {!rowHasOutcome
                            ? <span className="dpc-save-hint">Pick at least one outcome to enable saving</span>
                            : rowFollowUpBlocked && <span className="dpc-save-hint">Pick a follow-up date and time in the future</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
          )}
        </div>
      ) : !hasDispContext && !(isActive && lastSubmittedDisposition) ? (
        // No disposition context. On a live call this means by-context
        // returned no form (or hasn't answered yet) — the field grid below
        // used to render anyway with default first/last name/number fields,
        // looking like a real disposition the agent had to fill.
        <div className="dpc-empty">
          {isActive
            ? 'No disposition form for this call.'
            : 'No disposition — this fills in once a call starts or a pending item is opened.'}
        </div>
      ) : !hasDispContext && lastSubmittedDisposition ? (
        // Still on the same call after submitting — dispositionMeta/values
        // both clear the instant submit succeeds, so without this the
        // agent would otherwise see a completely blank field grid here
        // (first name / last name / contact number etc, all empty) as if
        // inviting a second disposition for the call they just saved.
        <div className="dpc-empty">
          <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span>
              Disposition {lastSubmittedDisposition.edited ? 'updated' : 'saved'}{lastSubmittedDisposition.formName ? ` — ${lastSubmittedDisposition.formName}` : ''}
            </span>
            {lastSubmittedDisposition.id && lastSubmittedDisposition.meta && onEditSubmitted && (
              <button
                type="button"
                className="dpc-save"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px' }}
                onClick={onEditSubmitted}
                title="Edit this disposition"
                aria-label="Edit disposition"
              >
                <FiEdit3 size={12} /> Edit
              </button>
            )}
          </div>
          {lastSubmittedDisposition.outcomes?.length > 0 && (
            <div className="dpc-outcomes" style={{ justifyContent: 'center' }}>
              {lastSubmittedDisposition.outcomes.map((o) => (
                <span key={o} className="dpc-outcome dpc-outcome--active" style={{ cursor: 'default' }}>
                  {o}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : (
      <>
      {/* Call details — mainly for a pending item reopened from the
       * Pending tab, where there's no live call on screen to glance at for
       * context; also shown during a genuine live call since it's the
       * same useful summary either way. */}
      {(dispositionMeta?.campaign_name || dispositionMeta?.callerNumber || dispositionMeta?.call_direction) && (
        <div className="dpc-call-info">
          {dispositionMeta?.campaign_name && (
            <span className="dpc-call-info__item"><strong>Campaign:</strong> {dispositionMeta.campaign_name}</span>
          )}
          {dispositionMeta?.callerNumber && (
            <span className="dpc-call-info__item">
              <strong>Number:</strong> {maskNumbers ? maskNumber(dispositionMeta.callerNumber) : dispositionMeta.callerNumber}
            </span>
          )}
          {dispositionMeta?.call_direction && (
            <span className="dpc-call-info__item"><strong>Direction:</strong> {dispositionMeta.call_direction}</span>
          )}
        </div>
      )}

      {/* Previously saved on this caller — collapsible */}
      {previousNotes.length > 0 && (
        <div className={`dpc-history${historyOpen ? '' : ' dpc-history--closed'}`}>
          <button
            type="button"
            className="dpc-history-head"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-expanded={historyOpen}
          >
            <FiClock size={11} className="dpc-history-ico" />
            <span className="dpc-history-label">
              Previously saved on this caller ({previousNotes.length})
            </span>
            <FiChevronDown size={12} className={`dpc-history-caret${historyOpen ? ' dpc-history-caret--open' : ''}`} />
          </button>
          {historyOpen && (
            <div className="dpc-history-body">
              {previousNotes.map((n) => (
                <div className="dpc-history-row" key={n.id || `${n.when}-${n.note}`}>
                  <div className="dpc-history-meta">
                    <span className="dpc-history-when">{n.when}</span>
                    {n.direction && (
                      <>
                        <span className="dpc-history-sep">·</span>
                        <span className="dpc-history-agent">{n.direction}</span>
                      </>
                    )}
                    {n.agent && (
                      <>
                        <span className="dpc-history-sep">·</span>
                        <span className="dpc-history-agent">{n.agent}</span>
                      </>
                    )}
                  </div>
                  <div className="dpc-history-note">{n.note}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Outcome — same tappable chips as before, but each one toggles
       * independently now (multi-select) instead of the old pick-one
       * behavior; a free-typed "Others" box merges in alongside them —
       * both write into dispositionValues.remarks_status. */}
      <div className="dpc-section">
        <div className="dpc-section-label">Outcome</div>
        <div className="dpc-outcomes">
          {outcomes.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => toggleOutcome(o)}
              className={`dpc-outcome${selectedOutcomes.includes(o) ? ' dpc-outcome--active' : ''}`}
            >
              {o}
            </button>
          ))}
        </div>
        <input
          type="text"
          className="dpc-field-input"
          style={{ marginTop: 8 }}
          placeholder="Others (comma separated)"
          value={othersText}
          onChange={(e) => {
            setOthersText(e.target.value);
            commitOutcomes(selectedOutcomes, e.target.value);
          }}
        />
      </div>

      {/* Disposition form fields — the admin-configured required/custom
       * fields of the form resolved by by-context. No fields -> no section
       * (there is no hardcoded fallback form). */}
      {fields.length > 0 && (
      <div className="dpc-section">
        <div className="dpc-section-label">Details</div>
        <div className="dpc-field-grid">
          {fields.map((field) => {
              const name = String(field?.field_name || '').trim();
              if (!name) return null;
              return (
                <DispositionFieldInput
                  key={name}
                  field={field}
                  value={dispositionValues?.[name]}
                  originalValue={dispositionMeta?.callerNumber}
                  error={dispositionErrors?.[name]}
                  maskNumbers={maskNumbers}
                  onChange={(next) => {
                    setDispositionValues?.((prev) => ({ ...(prev || {}), [name]: next }));
                    clearFieldError(name);
                  }}
                />
              );
            })}
        </div>
      </div>
      )}

      {/* Follow-up — only when this disposition form's campaign enables it */}
      {dispositionMeta?.dispositionForm?.follow_ups_enabled ? (
        <FollowUpFields
          values={dispositionValues}
          errors={dispositionErrors}
          onValues={(patch) => setDispositionValues?.((prev) => ({ ...(prev || {}), ...patch }))}
          onErrors={setDispositionErrors}
        />
      ) : null}

      {/* Footer */}
      {onSubmitAndExit ? (
      <div className="dpc-foot">
        <button
          type="button"
          onClick={saveMain}
          disabled={!canSave || dispositionSubmitting}
          className="dpc-save dpc-save--primary"
        >
          {dispositionSubmitting ? 'Saving…' : editingSubmitted ? 'Save changes' : isActive ? 'Save' : 'Save & Next Call'}
        </button>
        {editingSubmitted ? (
          <button
            type="button"
            onClick={onCancelEditSubmitted}
            disabled={dispositionSubmitting}
            className="dpc-save"
          >
            Cancel
          </button>
        ) : canSaveAndExit && (
          <button
            type="button"
            onClick={saveAndExit}
            disabled={!canSave || dispositionSubmitting}
            title="Save now and stay in wrap-up until the timer ends — no calls till then"
            className="dpc-save"
          >
            Save & Exit
          </button>
        )}
        {saveHint && <span className="dpc-save-hint">{saveHint}</span>}
      </div>
      ) : (
      <div className="dpc-foot">
        <button
          type="button"
          onClick={submit}
          disabled={!canSave || dispositionSubmitting}
          className="dpc-save dpc-save--primary"
        >
          {dispositionSubmitting ? 'Saving…' : editingSubmitted ? 'Save changes' : 'Save & close'}
        </button>
        {editingSubmitted ? (
          <button
            type="button"
            onClick={onCancelEditSubmitted}
            disabled={dispositionSubmitting}
            className="dpc-save"
          >
            Cancel
          </button>
        ) : !dispositionMeta?.is_lead_recall && (
          <button
            type="button"
            onClick={submitAndNext}
            disabled={!canSave || dispositionSubmitting || isActive}
            title={isActive ? 'End this call first — "& next" places the next call for you' : undefined}
            className="dpc-save"
          >
            {dispositionMeta?.is_lead ? 'Save Dispose & Next Lead' : 'Save & next call'}
          </button>
        )}
        {saveHint && <span className="dpc-save-hint">{saveHint}</span>}
      </div>
      )}
      </>
      )}
    </div>
  );
}
